import React from "react";
import classNames from "classnames";
import { isNumber } from "@common/helpers/typeGuards";
import { currencyFormatter } from "@common/helpers/currency";
import { VStack } from "@design-system/VStack/VStack";
import { DetailsAccordionItem } from "@design-system/Accordion/DetailsAccordionItem";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import { Link } from "@design-system/Link/Link";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";
import { CounterpartyBooksSummary } from "./CounterpartyEditor/CounterpartyBooksSummary";
import { CounterpartyAttachments } from "./CounterpartyAttachments";
import { CounterpartyRecordCount } from "./CounterpartyRecordCount";
import { CounterpartyTaxRows } from "./CounterpartyTaxRows";
import { useCounterpartyRecordOpeners } from "./useCounterpartyRecordOpeners";
import styles from "./CounterpartyDetailsPanel.module.scss";

const amountClassName = classNames(styles.value, styles.amount);

interface Props {
    row: CounterpartyDirectoryViewRow;
}

export const CounterpartyDetailsAccordionItem: React.FC<Props> = ({ row }) => {
    const cp = row.counterparty;
    const { openJournalEntries, openTransactions, openInvoices, openBills } =
        useCounterpartyRecordOpeners(cp?.id ?? "");

    return (
        <DetailsAccordionItem value="details" label="Details">
            <VStack gap={4}>
                <DescriptionListItem label="Journal entries">
                    <CounterpartyRecordCount
                        count={row.journalEntryLinesCount ?? 0}
                        recordName="journal entry"
                        onOpen={cp ? openJournalEntries : undefined}
                    />
                </DescriptionListItem>
                {isNumber(row.debit) && (
                    <DescriptionListItem label="Debit">
                        <span className={amountClassName}>
                            {currencyFormatter.format(row.debit)}
                        </span>
                    </DescriptionListItem>
                )}
                {isNumber(row.credit) && (
                    <DescriptionListItem label="Credit">
                        <span className={amountClassName}>
                            {currencyFormatter.format(row.credit)}
                        </span>
                    </DescriptionListItem>
                )}
                {cp && (
                    <>
                        <DescriptionListItem label="Transactions">
                            <span className={styles.value}>
                                <Link size="xs" onClick={openTransactions}>
                                    View transactions
                                </Link>
                            </span>
                        </DescriptionListItem>
                        <DescriptionListItem label="Bills">
                            <CounterpartyRecordCount
                                count={row.billCount ?? 0}
                                recordName="bill"
                                onOpen={openBills}
                            />
                        </DescriptionListItem>
                        <DescriptionListItem label="Invoices">
                            <CounterpartyRecordCount
                                count={row.invoiceCount ?? 0}
                                recordName="invoice"
                                onOpen={openInvoices}
                            />
                        </DescriptionListItem>
                        <CounterpartyTaxRows counterparty={cp} />
                        <CounterpartyBooksSummary counterparty={cp} />
                    </>
                )}
                {cp?.note && (
                    <DescriptionListItem label="Note" alignTop>
                        <span className={classNames(styles.value, styles.note)}>
                            {cp.note}
                        </span>
                    </DescriptionListItem>
                )}
                {cp && (
                    <DescriptionListItem label="Attachments" alignTop>
                        <CounterpartyAttachments counterparty={cp} />
                    </DescriptionListItem>
                )}
            </VStack>
        </DetailsAccordionItem>
    );
};
